import "albertine-shared-web/variables.tailwind.css";
import classNames from "classnames";
import type { Location } from "history";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import "./App.css";
import { useAgent } from "./common/hooks/useAgents";
import { useSuppliers } from "./common/hooks/useSuppliers";
import { useVersionCheck } from "./common/hooks/useVersionCheck";
import AgentPreferences from "./components/AgentPreferences/AgentPreferences";
import FetchAgent from "./components/AgentPreferences/FetchAgent";
import { AgentsList } from "./components/AgentsList/AgentsList";
import CatalogViewBeta from "./components/CatalogBeta/Catalog";
import WithGoogle from "./components/CatalogBeta/WithGoogle";
import TrackedScreen from "./components/Common/Analytics/TrackedScreen";
import ToastMessages from "./components/Common/ToastMessage";
import ComponentLibrary from "./components/ComponentLibrary/ComponentLibrary";
import ContentThemes from "./components/Content/ContentThemes";
import WithThemeQuery from "./components/Content/WithThemeQuery";
import DevelopmentHeader from "./components/Development/DevelopmentHeader";
import FetchMemberAndConversation, {
    FetchMemberAndConversationFromRoute,
} from "./components/MainView/FetchMemberAndConversation";
import { FetchRequestFromRoute } from "./components/MainView/FetchRequest";
import MemberWithRequestListView from "./components/MainView/MemberWithRequestListView";
import MemberWithSelectedRequestView from "./components/MainView/MemberWithSelectedRequestView";
import MobileMemberView from "./components/MainView/MobileMemberView";
import MembersList from "./components/MembersList/MembersList";
import Menu from "./components/Menu/Menu";
import MobileMenu from "./components/Menu/MobileMenu";
import MessagesList from "./components/MessagesList/MessagesList";
import { PaymentsListView } from "./components/Payments/PaymentsListView";
import RequestListView from "./components/RequestListView/RequestListView";
import Settings from "./components/Settings/Settings";
import TasksListView from "./components/Tasks/TasksListView";
import DeveloperMode from "./developer/DeveloperMode";
import { isLocalDevelopmentEnvironment } from "./Firebase";
import { useLiveUpdates } from "./liveUpdates/useLiveUpdates";
import { useClaims } from "./utils/claims.util";
import routes, { routeSchemas } from "./utils/routes";
import { isMobile } from "./utils/window.util";

const App = function App(props: { currentAgentId: string }) {
    const { currentAgentId } = props;

    const location = useLocation<Location>() as Location;
    const backgroundLocation = location.state
        ? (location.state as Location)
        : undefined;

    const currentAgent = useAgent(currentAgentId);
    const { isAdmin } = useClaims();

    const { data: suppliers } = useSuppliers();

    useVersionCheck();
    useLiveUpdates();

    if (!currentAgent) return null;

    const app = (
        <div className={classNames("app", isMobile() ? "mobile" : "desktop")}>
            {isLocalDevelopmentEnvironment && !isMobile() && <DevelopmentHeader />}
            <div className="tabs">
                <Route exact path="/">
                    <Redirect to="/messages/all" />
                </Route>

                {isMobile() ? (
                    <MobileMenu currentAgent={currentAgent} />
                ) : (
                    <Menu currentAgent={currentAgent} />
                )}

                <Switch location={backgroundLocation || location}>
                    <Route path="/messages">
                        <TrackedScreen
                            screen="lmt_messages_screen"
                            attributes={{
                                lmt_a_agent: currentAgentId,
                            }}
                        >
                            <MessagesList />
                        </TrackedScreen>
                    </Route>

                    <Route path="/requests">
                        {currentAgent && (
                            <TrackedScreen
                                screen="lmt_requests_screen"
                                attributes={{
                                    lmt_a_agent: currentAgentId,
                                }}
                            >
                                <RequestListView currentAgent={currentAgent} />
                            </TrackedScreen>
                        )}
                    </Route>

                    <Route path="/tasks/:filter">
                        <TrackedScreen
                            screen="lmt_tasks_screen"
                            attributes={{
                                lmt_a_agent: currentAgentId,
                            }}
                        >
                            <TasksListView />
                        </TrackedScreen>
                    </Route>

                    <Route path="/payments">
                        <TrackedScreen
                            screen="lmt_payments_screen"
                            attributes={{
                                lmt_a_agent: currentAgentId,
                            }}
                        >
                            <PaymentsListView />
                        </TrackedScreen>
                    </Route>

                    <Route
                        path={[
                            routeSchemas.member,
                            routeSchemas.memberWithMessage,
                            "/member/:memberId/articles/category/:category",
                            "/member/:memberId/articles/:id",
                        ]}
                    >
                        <TrackedScreen
                            screen="lmt_member_chat_general_screen"
                            attributes={{
                                lmt_a_agent: currentAgentId,
                            }}
                        >
                            <FetchMemberAndConversationFromRoute
                                path={routeSchemas.member}
                            >
                                {(member, conversation) =>
                                    isMobile() ? (
                                        <MobileMemberView
                                            currentAgent={currentAgent}
                                            conversation={conversation}
                                            member={member}
                                            suppliers={suppliers || []}
                                        />
                                    ) : (
                                        <MemberWithRequestListView
                                            currentAgent={currentAgent}
                                            conversation={conversation}
                                            member={member}
                                            suppliers={suppliers || []}
                                        />
                                    )
                                }
                            </FetchMemberAndConversationFromRoute>
                        </TrackedScreen>
                    </Route>

                    <Route
                        path={[
                            routeSchemas.request,
                            routeSchemas.requestBooking,
                            routeSchemas.requestWithMessage,
                        ]}
                    >
                        <TrackedScreen
                            screen="lmt_member_chat_request_screen"
                            attributes={{
                                lmt_a_agent: currentAgentId,
                            }}
                        >
                            <FetchRequestFromRoute path={routeSchemas.request}>
                                {(request) => (
                                    <FetchMemberAndConversation member={request.member}>
                                        {(member, conversation) =>
                                            isMobile() ? (
                                                <MobileMemberView
                                                    currentAgent={currentAgent}
                                                    conversation={conversation}
                                                    member={member}
                                                    request={request}
                                                    suppliers={suppliers || []}
                                                />
                                            ) : (
                                                <MemberWithSelectedRequestView
                                                    currentAgent={currentAgent}
                                                    conversation={conversation}
                                                    member={member}
                                                    request={request}
                                                    suppliers={suppliers || []}
                                                />
                                            )
                                        }
                                    </FetchMemberAndConversation>
                                )}
                            </FetchRequestFromRoute>
                        </TrackedScreen>
                    </Route>

                    <Route path="/members">
                        <TrackedScreen
                            screen="lmt_members_screen"
                            attributes={{
                                lmt_a_agent: currentAgentId,
                            }}
                        >
                            <MembersList currentAgent={currentAgent} />
                        </TrackedScreen>
                    </Route>

                    <Route path="/agents">
                        <TrackedScreen
                            screen="lmt_agents_screen"
                            attributes={{
                                lmt_a_agent: currentAgentId,
                            }}
                        >
                            <AgentsList />
                        </TrackedScreen>
                    </Route>

                    <Route path="/settings">
                        {isAdmin ? (
                            <TrackedScreen
                                screen="lmt_settings_screen"
                                attributes={{
                                    lmt_a_agent: currentAgentId,
                                }}
                            >
                                <Settings />
                            </TrackedScreen>
                        ) : (
                            <Redirect to="/" />
                        )}
                    </Route>

                    <Route path="/preferences">
                        <TrackedScreen
                            screen="lmt_preferences_screen"
                            attributes={{
                                lmt_a_agent: currentAgentId,
                            }}
                        >
                            <FetchAgent agent={currentAgentId}>
                                {(agent) => <AgentPreferences agent={agent} />}
                            </FetchAgent>
                        </TrackedScreen>
                    </Route>

                    <Route path={routes.catalog}>
                        <TrackedScreen
                            screen="lmt_standalone_catalog_beta_screen"
                            attributes={{
                                lmt_a_agent: currentAgentId,
                            }}
                        >
                            <WithGoogle>
                                {(googlePlaceService) => (
                                    <CatalogViewBeta
                                        currentAgent={currentAgent}
                                        googlePlaceService={googlePlaceService}
                                        suppliers={suppliers ?? []}
                                        request={undefined}
                                        member={undefined}
                                        cartItems={[]}
                                        standalone
                                    />
                                )}
                            </WithGoogle>
                        </TrackedScreen>
                    </Route>

                    <Route path={routeSchemas.themes}>
                        <TrackedScreen
                            screen="lmt_content_themes_screen"
                            attributes={{
                                lmt_a_agent: currentAgentId,
                            }}
                        >
                            <WithThemeQuery>
                                {(pathParams, queryParams) => (
                                    <ContentThemes
                                        currentAgent={currentAgent}
                                        suppliers={suppliers ?? []}
                                        pathParams={pathParams}
                                        queryParams={queryParams}
                                    />
                                )}
                            </WithThemeQuery>
                        </TrackedScreen>
                    </Route>
                </Switch>
            </div>

            <ToastMessages />
        </div>
    );

    return (
        <Switch>
            <Route path="/component-library">
                <ComponentLibrary />
            </Route>

            {import.meta.env.REACT_APP_ENABLE_DEV_MODE === "true" && (
                <Route path="/developer">
                    <DeveloperMode />
                </Route>
            )}

            <Route>{app}</Route>
        </Switch>
    );
};

export default App;
